/**
 * @fileoverview CaregiversPage Component
 * 
 * This module provides the main caregivers management interface for the application.
 * It displays a paginated, searchable, and filterable list of caregivers with options
 * to add, edit, and delete caregiver records.
 * 
 * @exports FilterButton - Reusable dropdown filter component
 * @exports CaregiversPage - Main page component for caregiver management
 * 
 * @requires react - For component state and lifecycle management
 * @requires ../hooks/useWindowSize - Custom hook for responsive design
 * @requires ../services/api - API service for backend communication
 * @requires ../components/LoadingSpinner - Loading state component
 * @requires ../components/ErrorMessage - Error display component
 * 
 * @example
 * // Usage in parent component
 * <CaregiversPage
 *   onFiltersChange={(filters) => console.log(filters)}
 *   onCaregiverClick={(id) => navigate(`/caregivers/${id}`)}
 *   onAddCaregiver={() => setShowAddModal(true)}
 *   onEditCaregiver={(caregiver) => setEditingCaregiver(caregiver)}
 * />
 */

import { useState, useEffect, useCallback } from "react";
import useWindowSize from "../hooks/useWindowSize";
import apiService from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorMessage from "../components/ErrorMessage";
import ConfirmationModal from "../components/ConfirmationModal";
import NotificationModal from "../components/NotificationModal";
import profileIcon from "../assets/caregivers.svg";
import trashIcon from "../assets/Trash.svg";
import "../styles/pages/CaregiversPage.css";

const W = "#ffffff";

/* ═══════════════════════ FILTER BUTTON COMPONENT (EXPORTED) ═══════════════════════ */

/**
 * FilterButton Component
 * 
 * A reusable dropdown filter button with custom styling and mobile responsiveness.
 * Displays the current filter value and allows selection from a list of options.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.label - The label text displayed before the value (e.g., "Role", "Shift")
 * @param {string} props.value - The currently selected filter value
 * @param {string[]} props.options - Array of available filter options
 * @param {Function} props.onChange - Callback function invoked when a new option is selected
 * @param {boolean} props.isMobile - Flag indicating if the component is in mobile view
 * 
 * @returns {JSX.Element} A styled dropdown filter button
 * 
 * @example
 * <FilterButton
 *   label="Role"
 *   value="Nurse"
 *   options={["All", "Nurse", "Doctor", "Admin"]}
 *   onChange={(newValue) => setRole(newValue)}
 *   isMobile={false}
 * />
 */
export function FilterButton({ label, value, options, onChange, isMobile }) {
  // Track dropdown open/closed state
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="caregivers-filter-button-container">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`caregivers-filter-button ${value !== "All" ? 'caregivers-filter-button--active' : ''} ${isMobile ? 'mobile' : ''}`}
      >
        <span className="caregivers-filter-button__label">{label}:</span>
        <span>{value}</span>
        <svg 
          className={`caregivers-filter-button__arrow ${isOpen ? 'open' : ''}`}
          width="12" 
          height="12" 
          viewBox="0 0 24 24" 
          fill="none"
        >
          <path d="M6 9l6 6 6-6"/>
        </svg>
      </button>

      {/* Dropdown menu - only rendered when open */}
      {isOpen && (
        <>
          {/* Invisible backdrop to close dropdown when clicking outside */}
          <div
            onClick={() => setIsOpen(false)}
            className="caregivers-filter-dropdown-overlay"
          />
          {/* Dropdown options container */}
          <div className="caregivers-filter-dropdown">
            {options.map((option) => (
              <button
                key={option}
                onClick={() => {
                  onChange(option);
                  setIsOpen(false);
                }}
                className={`caregivers-filter-dropdown__option ${value === option ? 'active' : ''} ${isMobile ? 'mobile' : ''}`}
              >
                {option}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════ CAREGIVERS PAGE ═══════════════════════ */

/**
 * CaregiversPage Component
 * 
 * Main page component for managing caregivers. Provides a comprehensive interface
 * for viewing, searching, filtering, and managing caregiver records with pagination.
 * 
 * Features:
 * - Real-time search across name, ID, role, and badge ID
 * - Multi-criteria filtering (role, shift, status)
 * - Paginated results (10 records per page)
 * - Responsive design for mobile and desktop
 * - Add, edit, and delete caregiver functionality
 * - Loading and error states
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.onFiltersChange - Callback to expose filter state to parent component
 * @param {Function} props.onCaregiverClick - Callback invoked when a caregiver card is clicked
 * @param {Function} props.onAddCaregiver - Callback to trigger add caregiver flow
 * @param {Function} props.onEditCaregiver - Callback to trigger edit caregiver flow
 * 
 * @returns {JSX.Element} The caregivers management page
 * 
 * @example
 * <CaregiversPage
 *   onFiltersChange={(filters) => setSidebarFilters(filters)}
 *   onCaregiverClick={(id) => navigate(`/caregivers/${id}`)}
 *   onAddCaregiver={() => setShowAddModal(true)}
 *   onEditCaregiver={(caregiver) => setEditingCaregiver(caregiver)}
 * />
 */
export default function CaregiversPage({ onFiltersChange, onCaregiverClick, onAddCaregiver, onEditCaregiver, refreshTrigger }) {
  /* ═══════════════════════ STATE MANAGEMENT ═══════════════════════ */
  
  /** @state {Array} caregivers - Complete list of caregivers from API */
  const [caregivers, setCaregivers] = useState([]);
  
  /** @state {Array} filteredCaregivers - Filtered and searched caregiver list */
  const [filteredCaregivers, setFilteredCaregivers] = useState([]);
  
  /** @state {boolean} loading - Loading state for API calls */
  const [loading, setLoading] = useState(true);
  
  /** @state {Error|null} error - Error state for API failures */
  const [error, setError] = useState(null);
  
  /** @state {string} searchTerm - Current search query */
  const [searchTerm, setSearchTerm] = useState("");
  
  /** @state {string} selectedRole - Currently selected role filter */
  const [selectedRole, setSelectedRole] = useState("All");
  
  /** @state {string} selectedShift - Currently selected shift filter */
  const [selectedShift, setSelectedShift] = useState("All");
  
  /** @state {string} selectedStatus - Currently selected status filter */
  const [selectedStatus, setSelectedStatus] = useState("All");
  
    /** @state {number} currentPage - Current pagination page number */
  const [currentPage, setCurrentPage] = useState(1);
  
  /** @constant {number} recordsPerPage - Number of records to display per page */
  const recordsPerPage = 10;

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [caregiverToDelete, setCaregiverToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Notification modal state
  const [notification, setNotification] = useState({ show: false, type: 'info', message: '' });

  /** @hook {Object} useWindowSize - Custom hook for responsive design */
  const { isMobile } = useWindowSize();

    /* ═══════════════════════ EFFECTS ═══════════════════════ */
  
  /**
   * Effect: Expose filter state to parent component
   * 
   * Notifies parent component of current filter values and their setters,
   * allowing external components (like Sidebar) to control filters.
   */
  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange({
        selectedRole,
        setSelectedRole,
        selectedShift,
        setSelectedShift,
        selectedStatus,
        setSelectedStatus,
      });
    }
  }, [selectedRole, selectedShift, selectedStatus, onFiltersChange]);

    /**
     * Effect: Load caregivers on component mount and when refreshTrigger changes
     * 
     * Fetches the initial list of caregivers from the API when the component
     * is first rendered or when the refreshTrigger prop changes.
     */
    useEffect(() => {
      loadCaregivers();
    }, [refreshTrigger]);

    /* ═══════════════════════ DATA LOADING ═══════════════════════ */
  
  /**
   * Load caregivers from API
   * 
   * Fetches all caregiver records from the backend API and transforms them
   * into a standardized format for display. Handles missing data gracefully
   * by providing default values.
   * 
   * @async
   * @function loadCaregivers
   * @returns {Promise<void>}
   * 
   * @throws {Error} Sets error state if API call fails
   * 
   * Data transformation:
   * - Constructs display name from display_name, first_name, or last_name
   * - Removes 'BADGE-' prefix from badge IDs
   * - Provides 'N/A' defaults for missing fields
   * - Generates fallback IDs for records without caregiver_id
   */
  const loadCaregivers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getAllCaregivers();
      
      // Handle empty or invalid API response
      if (!data || !data.caregivers || data.caregivers.length === 0) {
        console.warn('No caregivers data received from API');
        setCaregivers([]);
        return;
      }
      
      // Transform API data into display format
      const transformedCaregivers = data.caregivers.map((c, index) => {
        // Construct display name with fallback logic
        let displayName = 'Unknown Caregiver';
        if (c.display_name) {
          displayName = c.display_name;
        } else if (c.first_name || c.last_name) {
          displayName = `${c.first_name || ''} ${c.last_name || ''}`.trim();
        }
        
                // Return standardized caregiver object
                // Keep original values for editing, but provide display versions
                return {
                  caregiver_id: c.caregiver_id || `CG-unknown-${index}`,
                  name: displayName,
                  role: c.role || 'N/A',
                  primary_shift: c.primary_shift || 'N/A',
                  shift: c.primary_shift || 'N/A', // Add shift alias for EditModal
                  badge_id: c.badge_id || 'N/A', // Keep original with prefix
                  employee_id: c.employee_id || 'N/A', // Keep original with prefix
                  status: c.status || 'ACTIVE',
                  email: c.email || 'N/A',
                  phone: c.phone || 'N/A',
                  // Add display versions for card
                  badge_id_display: (c.badge_id || '').replace('BADGE-', '') || 'N/A',
                  employee_id_display: (c.employee_id || '').replace('EMP-', '') || 'N/A',
                };
      });
      
      setCaregivers(transformedCaregivers);
    } catch (err) {
      console.error('Failed to load caregivers:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

    /* ═══════════════════════ FILTERING LOGIC ═══════════════════════ */
  
  /**
   * Filter caregivers based on search term and selected filters
   * 
   * Applies multiple filter criteria in sequence:
   * 1. Search term (matches name, ID, role, or badge ID)
   * 2. Role filter
   * 3. Shift filter
   * 4. Status filter
   * 
   * Resets pagination to page 1 after filtering.
   * 
   * @function filterCaregivers
   * @returns {void}
   * 
   * @memoized Uses useCallback to prevent unnecessary re-renders
   */
  const filterCaregivers = useCallback(() => {
    let filtered = [...caregivers];

    // Apply search term filter (case-insensitive, multiple fields)
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(c => 
        (c.name || "").toLowerCase().includes(search) ||
        (c.caregiver_id || "").toLowerCase().includes(search) ||
        (c.role || "").toLowerCase().includes(search) ||
        (c.badge_id || "").toLowerCase().includes(search)
      );
    }

    // Apply role filter
    if (selectedRole !== "All") {
      filtered = filtered.filter(c => c.role === selectedRole);
    }

    // Apply shift filter
    if (selectedShift !== "All") {
      filtered = filtered.filter(c => c.primary_shift === selectedShift);
    }

    // Apply status filter
    if (selectedStatus !== "All") {
      filtered = filtered.filter(c => c.status === selectedStatus);
    }

    setFilteredCaregivers(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [caregivers, searchTerm, selectedRole, selectedShift, selectedStatus]);

    /**
   * Effect: Re-filter caregivers when dependencies change
   * 
   * Automatically applies filters whenever the filter function or its
   * dependencies (caregivers, search term, selected filters) change.
   */
  useEffect(() => {
    filterCaregivers();
  }, [filterCaregivers]);

    /* ═══════════════════════ EVENT HANDLERS ═══════════════════════ */
  
    /**
     * Handle caregiver deletion - Show confirmation modal
     * 
     * @param {Object} caregiver - The caregiver object to delete
     */
    const handleDeleteCaregiver = (caregiver) => {
      setCaregiverToDelete(caregiver);
      setShowDeleteModal(true);
    };

    /**
     * Confirm and execute caregiver deletion
     * 
     * Calls the backend API to delete the caregiver and refreshes the list.
     * 
     * @async
     * @function confirmDeleteCaregiver
     * @returns {Promise<void>}
     * 
     * @throws {Error} Displays error notification if deletion fails
     */
    const confirmDeleteCaregiver = async () => {
      if (!caregiverToDelete) return;

      try {
        setDeleteLoading(true);
        await apiService.deleteCaregiver(caregiverToDelete.caregiver_id);
      
        // Reload the caregivers list
        await loadCaregivers();
      
        // Close delete modal
        setShowDeleteModal(false);
        setCaregiverToDelete(null);
      
        // Show success notification
        setNotification({
          show: true,
          type: 'success',
          message: 'Caregiver deleted successfully!'
        });
      } catch (err) {
        console.error('Failed to delete caregiver:', err);
      
        // Show error notification
        setNotification({
          show: true,
          type: 'error',
          message: `Failed to delete caregiver: ${err.message || 'Please try again.'}`
        });
      } finally {
        setDeleteLoading(false);
      }
    };

    /* ═══════════════════════ LOADING & ERROR STATES ═══════════════════════ */
  
    // Display loading spinner while fetching data
  if (loading) {
    return (
      <main className="caregivers-loading-container">
        <LoadingSpinner message="Loading caregivers..." />
      </main>
    );
  }

  // Display error message if API call failed
  if (error) {
    return (
      <main className="caregivers-loading-container">
        <ErrorMessage error={error} onRetry={loadCaregivers} />
      </main>
    );
  }

    /* ═══════════════════════ PAGINATION CALCULATIONS ═══════════════════════ */
  

        // Responsive layout values
  const gap = isMobile ? 8 : 10;

    // Calculate pagination values
  const totalPages = Math.ceil(filteredCaregivers.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredCaregivers.slice(indexOfFirstRecord, indexOfLastRecord);

  return (
        <main style={{
      flex: 1,
      padding: isMobile ? "8px" : "12px 60px",
      overflowY: "hidden",
      display: "flex",
      flexDirection: "column",
      gap,
      minWidth: 0,
      fontFamily: "'Segoe UI', sans-serif",
      color: W,
      position: "relative",
      zIndex: 1,
      paddingTop: isMobile ? 75 : 82,
      paddingBottom: isMobile ? 20 : 6,
      marginTop: 0,
      height: "100vh",
      boxSizing: "border-box",
    }}>
      
            {/* ═══════════════════════ SEARCH BAR & ADD CAREGIVER SECTION ═══════════════════════ */}
      <div className={`caregivers-search-bar ${isMobile ? 'mobile' : ''}`}>
                <button className="caregivers-label-btn">
          Caregivers
        </button>

                <div className={`caregivers-search-input-wrapper ${isMobile ? 'mobile' : ''}`}>
                    <div className="caregivers-search-icon">
            <svg width={24} height={24} viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="8" r="4"/>
              <path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2"/>
            </svg>
          </div>

                    <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="caregivers-search-input"
          />
                              {/* Clear search button - only shown when search term exists */}
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="caregivers-search-clear"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          )}
        </div>

                <span 
          onClick={() => onAddCaregiver && onAddCaregiver()}
          className="caregivers-add-label"
        >
          Add Caregiver
        </span>

                        {/* Add caregiver button with circular design */}
        <div className="caregivers-add-button-wrapper">
          {/* Outer green circle */}
          <div className="caregivers-add-button-outer" />
          {/* Inner button with icon */}
          <button 
            onClick={() => onAddCaregiver && onAddCaregiver()}
            className="caregivers-add-button-inner"
            title="Add Caregiver"
          >
            <img 
              src={profileIcon} 
              alt="Add Caregiver"
            />
          </button>
        </div>
      </div>

            {/* ═══════════════════════ CAREGIVERS GRID SECTION ═══════════════════════ */}
      <div className={`caregivers-grid-container ${isMobile ? 'mobile' : ''}`}>
                        {/* Scrollable grid container - 1 column on mobile, 2 on desktop */}
        <div className={`caregivers-grid ${isMobile ? 'mobile' : ''}`}>
                    {/* Empty state - shown when no caregivers match filters */}
          {currentRecords.length === 0 ? (
            <div className="caregivers-empty-state">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 8v4M12 16h.01"/>
              </svg>
              <p>
                {searchTerm ? "No caregivers found matching your search" : "No caregivers available"}
              </p>
            </div>
          ) : (
            /* Render caregiver cards for current page */
            currentRecords.map((caregiver) => (
              <CaregiverCard
                key={caregiver.caregiver_id}
                caregiver={caregiver}
                onDelete={handleDeleteCaregiver}
                onEdit={onEditCaregiver}
                onClick={() => onCaregiverClick && onCaregiverClick(caregiver.caregiver_id)}
                isMobile={isMobile}
              />
            ))
          )}
        </div>

                                        {/* ═══════════════════════ PAGINATION CONTROLS ═══════════════════════ */}
        {filteredCaregivers.length > 0 && (
          <div className={`caregivers-pagination ${isMobile ? 'mobile' : ''}`}>
                                    {/* Pagination info text */}
            <span className={`caregivers-pagination__info ${isMobile ? 'mobile' : ''}`}>
              Showing {indexOfFirstRecord + 1} - {Math.min(indexOfLastRecord, filteredCaregivers.length)} of {filteredCaregivers.length} caregivers
            </span>

                        {/* Pagination buttons container */}
            <div className="caregivers-pagination__controls">
                            {/* Previous page button */}
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="caregivers-pagination__nav-btn caregivers-pagination__nav-btn--prev"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18l-6-6 6-6"/>
                </svg>
                {!isMobile && <span>Prev</span>}
              </button>

                                          {/* Page number buttons - shows first, last, and pages near current */}
              {totalPages > 1 && Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => {
                // Only show first page, last page, and pages within 1 of current page
                if (
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setCurrentPage(pageNumber)}
                      className={`caregivers-pagination__page-btn ${currentPage === pageNumber ? 'caregivers-pagination__page-btn--active' : ''}`}
                    >
                      {pageNumber}
                    </button>
                  );
                }
                return null;
              })}

                                          {/* Next page button */}
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="caregivers-pagination__nav-btn caregivers-pagination__nav-btn--next"
              >
                {!isMobile && <span>Next</span>}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </button>
            </div>
                    </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onConfirm={confirmDeleteCaregiver}
        onCancel={() => {
          setShowDeleteModal(false);
          setCaregiverToDelete(null);
        }}
        title="Delete Caregiver"
        message={`Are you sure you want to remove ${caregiverToDelete?.name || 'this caregiver'}? This action cannot be undone.`}
        confirmText="Delete Caregiver"
        cancelText="Cancel"
        variant="danger"
        loading={deleteLoading}
        isMobile={isMobile}
        details={
          caregiverToDelete && (
            <>
              <div><strong>Caregiver ID:</strong> {caregiverToDelete.caregiver_id}</div>
              <div><strong>Role:</strong> {caregiverToDelete.role}</div>
              <div><strong>Badge ID:</strong> {caregiverToDelete.badge_id}</div>
              <div><strong>Shift:</strong> {caregiverToDelete.primary_shift}</div>
            </>
          )
        }
      />

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.show}
        onClose={() => setNotification({ ...notification, show: false })}
        type={notification.type}
        message={notification.message}
        autoDismiss={3}
        isMobile={isMobile}
      />
    </main>
  );
}

/* ═══════════════════════ CAREGIVER CARD COMPONENT ═══════════════════════ */

/**
 * CaregiverCard Component
 * 
 * Displays a single caregiver's information in a card format with interactive
 * edit and delete buttons. The card is clickable to view full caregiver details.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.caregiver - The caregiver data object
 * @param {string} props.caregiver.caregiver_id - Unique caregiver identifier
 * @param {string} props.caregiver.name - Caregiver's display name
 * @param {Function} props.onDelete - Callback invoked when delete button is clicked
 * @param {Function} props.onEdit - Callback invoked when edit button is clicked
 * @param {Function} props.onClick - Callback invoked when card is clicked
 * @param {boolean} props.isMobile - Flag indicating mobile view
 * 
 * @returns {JSX.Element} A styled caregiver card with hover effects
 * 
  * @example
 * <CaregiverCard
 *   caregiver={{ caregiver_id: "CG-20250301-0001", name: "John Doe" }}
 *   onDelete={(id) => handleDelete(id)}
 *   onEdit={(caregiver) => handleEdit(caregiver)}
 *   onClick={() => navigate(`/caregivers/${caregiver.caregiver_id}`)}
 *   isMobile={false}
 * />
 */
function CaregiverCard({ caregiver, onDelete, onEdit, onClick, isMobile }) {
  return (
    <div 
      onClick={onClick}
      className="caregiver-card"
    >
            {/* Profile icon */}
      <div className="caregiver-card__avatar">
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="8" r="4"/>
          <path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2"/>
        </svg>
      </div>

                  {/* Caregiver name */}
      <div className="caregiver-card__name-wrapper">
        <p className="caregiver-card__name">
          {caregiver.name || "Unknown Caregiver"}
        </p>
      </div>

                  {/* Edit button */}
      <button
        onClick={(e) => {
          e.stopPropagation(); // Prevent card click event
          onEdit && onEdit(caregiver);
        }}
        className="caregiver-card__edit-btn"
        title="Edit caregiver"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>
      </button>

                  {/* Delete button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent card click event
                      onDelete(caregiver);
                    }}
                    className="caregiver-card__delete-btn"
                    title="Remove caregiver"
                  >
                    <img src={trashIcon} alt="Delete" />
                  </button>
    </div>
  );
}
